// FeatureFlagService
// ------------------------------------------------------------------
// Talks to the backend FeatureFlag API (Core DB, shared across tenants).
//
// Two concerns:
//  1. Client read  -> GET /FeatureFlag/flags (env derived from host by the
//     backend, business scoped by token). Cached in localStorage and read
//     globally via isEnabled()/getAttributes().
//  2. Super-admin management -> /FeatureFlag (admin list + CRUD + toggle).
// ------------------------------------------------------------------
import ApiClient from '../ApiClient';

const STORAGE_KEY = 'featureFlags';
// Fired on `window` whenever the cached flags change, so already-mounted
// components (e.g. Sidebar) gated on a flag that arrives after their first
// render — fetchAndCache() runs un-awaited right before navigating to the
// dashboard — can re-check isEnabled() instead of staying stale until some
// unrelated re-render happens to occur.
const FLAG_UPDATE_EVENT = 'featureflags:updated';

// Backend flag names, as strings — the names themselves are the API contract
// (matched against the `name` field in GET /FeatureFlag/flags), not values we
// control here. Centralized so call sites use FeatureFlags.EMAIL_V2 instead of
// retyping/risking a typo in the raw string.
export const FeatureFlags = Object.freeze({
  EMAIL_V2: 'email_v2',
  ADS_COMPOSE_TAB: 'adds_compose_tab',
  WEBINAR_OR_MEETINGS_CREATION_EVENTS: 'webinar_or_meetings_creation_events',
});

export const ENVIRONMENTS = ['Local', 'Dev', 'Prod'];

// ---------------------------------------------------------------------------
// Feature flags live in ONE (Prod) database, so every flag call — admin AND
// client read — must hit the SINGLE Prod API host regardless of which frontend
// environment the user is on. The backend then derives the env from the
// request Origin/Referer header.
//
// TODO (switch when ready): set REACT_APP_FEATUREFLAG_API_BASE_URL in every
// .env* file to the single prod host, e.g. "https://api.prospectpulse.in/api".
// Until then it falls back to this env's normal API base for local testing.
// ---------------------------------------------------------------------------
const FEATURE_FLAG_API_BASE =
  process.env.REACT_APP_FEATUREFLAG_API_BASE_URL || process.env.REACT_APP_API_BASE_URL;

// Per-request override so we reuse ApiClient's token + 401-refresh interceptors
// while still targeting the fixed feature-flag host.
const ffConfig = (extra = {}) => ({ baseURL: FEATURE_FLAG_API_BASE, ...extra });

// Unwrap the AppResponse envelope { success, data, ... } from an axios response.
const unwrap = (resp) => resp?.data?.data;

const FeatureFlagService = {
  // ---------- Client read + global cache ----------

  // Fetch this user's flags for the current environment and cache them.
  // Call after login and on app boot (when a token exists).
  async fetchAndCache() {
    try {
      const flags = unwrap(await ApiClient.get('/FeatureFlag/flags', ffConfig())) ?? [];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
      window.dispatchEvent(new Event(FLAG_UPDATE_EVENT));
      return flags;
    } catch (error) {
      console.error('Failed to fetch feature flags:', error?.response?.data || error.message);
      return this.getCachedFlags();
    }
  },

  // Subscribe to cache updates (post-fetchAndCache). Returns an unsubscribe fn.
  onUpdate(callback) {
    window.addEventListener(FLAG_UPDATE_EVENT, callback);
    return () => window.removeEventListener(FLAG_UPDATE_EVENT, callback);
  },

  getCachedFlags() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  },

  // Is a flag enabled (for the cached environment)?
  isEnabled(name) {
    return this.getCachedFlags().some((f) => f.name === name && f.isEnabled);
  },

  // Parsed attributes object for a cached flag, or null.
  getAttributes(name) {
    const flag = this.getCachedFlags().find((f) => f.name === name);
    if (!flag) return null;
    try {
      return typeof flag.attributes === 'string' ? JSON.parse(flag.attributes) : flag.attributes;
    } catch {
      return null;
    }
  },

  clearCache() {
    localStorage.removeItem(STORAGE_KEY);
  },

  // ---------- Super-admin management ----------

  // All flags across all environments (one row per flag per env).
  async getAdminFlags() {
    return unwrap(await ApiClient.get('/FeatureFlag/admin', ffConfig())) ?? [];
  },

  // Creates the flag in ALL environments (rows default to Off, same attributes).
  async createFlag({ name, description, attributes, businessAdminIds, premiumFeature }) {
    return unwrap(await ApiClient.post('/FeatureFlag', {
      name,
      description,
      attributes,
      businessAdminIds,
      premiumFeature: !!premiumFeature,
    }, ffConfig()));
  },

  // Updates a single environment row (by its unique id). Never touches sibling envs.
  async updateFlag({ id, name, description, attributes, businessAdminIds, premiumFeature }) {
    return unwrap(await ApiClient.put('/FeatureFlag', {
      id,
      name,
      description,
      attributes,
      businessAdminIds,
      premiumFeature: !!premiumFeature,
    }, ffConfig()));
  },

  // Toggles a single environment row on/off by its unique id.
  async toggleFlag(id, isEnabled) {
    return unwrap(await ApiClient.patch('/FeatureFlag/state', { id, isEnabled }, ffConfig()));
  },

  // Deletes a single environment row by its unique id.
  async deleteFlag(id) {
    await ApiClient.delete(`/FeatureFlag/${id}`, ffConfig());
    return true;
  },
};

export default FeatureFlagService;
